import type { ISelectOptions } from 'context';
import type { MyCareerVacanciesQuery } from 'core/graphql';

/**
 * The caller's own state on a vacancy (spec 0018 §B2). Plain String on the
 * wire; `applied` outranks `saved` (a member may hold both rows, but the feed
 * shows the stronger badge). `null` = no interaction yet.
 */
export type VacancyInteraction = 'saved' | 'applied' | null;

/** A direction reference derived from the vacancy's parallel id/title arrays. */
export interface ICareerVacancyDirection {
  id: string;
  title: string;
}

/** Row shape for the vacancy feed — everything the card renders. */
export interface ICareerVacancy {
  id: string;
  title: string;
  companyName: string;
  /** External application link; opened in a new tab with rel=noopener. */
  applyUrl: string;
  location: string;
  /** Plain String on the wire: 'onsite' | 'hybrid' | 'remote' | '' */
  workMode: string;
  /** Plain String on the wire: 'intern' | 'junior' | 'middle' | 'senior' | '' */
  seniority: string;
  /** Plain String on the wire: 'full_time' | 'part_time' | 'internship' | 'contract' | '' */
  employmentType: string;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string;
  description: string;
  publishedAt: string | null;
  /** ISO-дата окончания приёма; '' если не задана. */
  expiresAt: string;
  directions: ICareerVacancyDirection[];
  skillNames: string[];
  /** THE badge/button state; never recomputed client-side. */
  interaction: VacancyInteraction;
}

const toInteraction = (value: string | null | undefined): VacancyInteraction => {
  if (value === 'saved' || value === 'applied') {
    return value;
  }
  return null;
};

/**
 * Zips the parallel `directionIds` / `directionTitles` arrays into references,
 * dropping any id without a matching title (defensive against a length skew).
 */
const zipDirections = (ids: string[], titles: string[]): ICareerVacancyDirection[] =>
  ids
    .map((id, index) => ({ id, title: titles[index] }))
    .filter((direction): direction is ICareerVacancyDirection => Boolean(direction.title));

/**
 * Normalizes `careerVacancies`. Returns `[]` for a non-member — the backend
 * answers with an empty list rather than an error (spec 0018 §B2).
 */
export const transformCareerVacancies = (
  data: MyCareerVacanciesQuery | undefined,
): ICareerVacancy[] =>
  (data?.careerVacancies ?? []).map((vacancy) => ({
    id: vacancy.id,
    title: vacancy.title,
    companyName: vacancy.companyName,
    applyUrl: vacancy.applyUrl,
    location: vacancy.location,
    workMode: vacancy.workMode,
    seniority: vacancy.seniority,
    employmentType: vacancy.employmentType,
    salaryMin: vacancy.salaryMin ?? null,
    salaryMax: vacancy.salaryMax ?? null,
    salaryCurrency: vacancy.salaryCurrency,
    description: vacancy.description,
    publishedAt: vacancy.publishedAt ?? null,
    expiresAt: vacancy.expiresAt ?? '',
    directions: zipDirections(vacancy.directionIds ?? [], vacancy.directionTitles ?? []),
    skillNames: vacancy.skillNames ?? [],
    interaction: toInteraction(vacancy.myInteraction),
  }));

/**
 * Direction filter options derived from the feed itself — there is no LMS
 * `careerDirections` query (it is ERP-scoped, spec 0018 §4). `previous` seeds
 * the accumulation so the dropdown keeps every direction ever seen and never
 * drops the currently-selected option when a filtered fetch narrows the list.
 */
export const collectDirectionOptions = (
  vacancies: ICareerVacancy[],
  previous: ISelectOptions[] = [],
): ISelectOptions[] => {
  const byId = new Map<string, string>();
  previous.forEach((option) => byId.set(option.value, option.label));
  vacancies.forEach((vacancy) =>
    vacancy.directions.forEach((direction) => byId.set(direction.id, direction.title)),
  );

  return Array.from(byId.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Human-readable salary range. Empty when the vacancy lists no salary — the
 * card then simply omits the line rather than showing a bare currency code.
 */
export const formatSalaryRange = (
  min: number | null,
  max: number | null,
  currency: string,
): string => {
  const cur = currency ? ` ${currency}` : '';
  // Deterministic thousands separator (plain ASCII space) — avoids toLocaleString's
  // ICU-dependent non-breaking space, which varies across Node versions.
  const money = (value: number) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (min && max) {
    return min === max ? `${money(min)}${cur}` : `${money(min)} – ${money(max)}${cur}`;
  }
  if (min) {
    return `${money(min)}${cur}`;
  }
  if (max) {
    return `${money(max)}${cur}`;
  }
  return '';
};
